import React, { useEffect, useState } from "react";
import TextField from "@material-ui/core/TextField";
import DateRangePicker from "@wojtekmaj/react-daterange-picker";

import CustomInput from "components/MainApp/atoms/Forms/MainInput";
import styles from "./styles.module.scss";
import { EventRepository } from "helpers/EventRepository";
import i18n from "i18n";

const dateLetter = [
  i18n.t("days.1"),
  i18n.t("days.2"),
  i18n.t("days.3"),
  i18n.t("days.4"),
  i18n.t("days.5"),
  i18n.t("days.6"),
  i18n.t("days.7")
];
const months = [
  i18n.t("months.numeric.1"),
  i18n.t("months.numeric.2"),
  i18n.t("months.numeric.3"),
  i18n.t("months.numeric.4"),
  i18n.t("months.numeric.5"),
  i18n.t("months.numeric.6"),
  i18n.t("months.numeric.7"),
  i18n.t("months.numeric.8"),
  i18n.t("months.numeric.9"),
  i18n.t("months.numeric.10"),
  i18n.t("months.numeric.11"),
  i18n.t("months.numeric.12")
];

const normalizeLocale = lang => {
  const supported = ["es-ES", "en"];
  return supported.includes(lang) ? lang : "es-ES";
};

const currentLang = normalizeLocale(i18n.language);

const DateInput = ({ label, selectedDates = [null, null], handleChange }) => {
  const [selectedDateInteral, handleDateChange] = useState(selectedDates);
  const [focused, setFocused] = useState(false);
  const [fromDate, setFromDate] = useState(undefined);
  const [validDate, setValidDate] = useState(undefined);

  /**
   * Syncs internal state when the parent component updates selectedDates.
   * This ensures the UI reflects external changes (e.g., clearing filters).
   */
  useEffect(() => {
    // Ensure input is a valid array to prevent runtime errors
    const sd = Array.isArray(selectedDates) ? selectedDates : [null, null];

    /**
     * Compare dates by actual value (.valueOf()) rather than memory reference.
     * This prevents infinite re-render loops caused by new array instances 
     * with identical date values.
     */
    const prevStart = selectedDateInteral[0]?.valueOf();
    const prevEnd = selectedDateInteral[1]?.valueOf();
    const nextStart = sd[0]?.valueOf();
    const nextEnd = sd[1]?.valueOf();

    // Trigger state update only if the date values have truly changed
    if (prevStart !== nextStart || prevEnd !== nextEnd) {
      handleDateChange(sd);
    }
  }, [selectedDates]);

  useEffect(() => {
    if (!focused) {
      if (selectedDateInteral[0] > selectedDateInteral[1]) {
        EventRepository.notificationSend({
          label: i18n.t("date.error.start.after.end"),
          type: "error"
        });
        handleDateChange(validDate);
        handleChange(validDate);
      }
    }
  }, [focused]);

  const _handleChange = _selectedDates => {
    handleDateChange(_selectedDates);
    if (_selectedDates[0] < _selectedDates[1]) {
      setValidDate(_selectedDates);
      handleChange(_selectedDates);
    } else {
      let valid = [..._selectedDates];
      valid[1] = _selectedDates[0];
      setValidDate(valid);
    }
  };

  return (
    <div
      className={` ${styles["date-range-container"]} ${
        focused ? styles["focused"] : ""
      }`}
    >
      <p className={styles["date-range-label"]}>{label}</p>
      <DateRangePicker
        onChange={_handleChange}
        value={selectedDateInteral}
        dayPlaceholder=""
        monthPlaceholder=""
        yearPlaceholder=""
        calendarAriaLabel="open"
        locale={currentLang}
        monthAriaLabeL="Month"
        formatShortWeekday={(locale, date) => dateLetter[date.getDay()]}
        formatMonthYear={(locale, date) =>
          months[date.getMonth()] + " " + date.getFullYear()
        }
        onCalendarOpen={() => setFocused(true)}
        onCalendarClose={() => setFocused(false)}
        calendarIcon={
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="20"
            viewBox="0 0 18 20"
            className={` ${styles["react-daterange-picker__calendar-button__icon"]} ${styles["react-daterange-picker__button__icon"]}`}
            fill="black"
          >
            <path
              id="ic_date_range_24px"
              d="M9,11H7v2H9Zm4,0H11v2h2Zm4,0H15v2h2Zm2-7H18V2H16V4H8V2H6V4H5A1.991,1.991,0,0,0,3.01,6L3,20a2,2,0,0,0,2,2H19a2.006,2.006,0,0,0,2-2V6A2.006,2.006,0,0,0,19,4Zm0,16H5V9H19Z"
              transform="translate(-3 -2)"
              fill="currentColor"
            />
          </svg>
        }
      />
    </div>
  );
};

export default DateInput;
